import PropTypes from 'prop-types'
import { useRef, forwardRef } from 'react'
import { useTheme } from '@emotion/react'
import { rgba } from 'polished'
import Tippy from '@tippyjs/react/headless'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import { getTransformOrigin } from 'chaoskit/src/helpers/utility'
import { Badge } from 'chaoskit/src/components'
import { motion, useAnimation } from 'framer-motion'

import Navigation, { navTransition } from './Navigation'

import Icon from '~components/Icon'
import ButtonLink from '~components/ButtonLink'
import TextLink from '~components/TextLink'
import blob from '~media/navigation/blob.svg'
import { ReactComponent as SlackSVG } from '~media/navigation/slack.svg'
import { ReactComponent as ChaosConfSVG } from '~media/navigation/chaosconf.svg'
import { ReactComponent as FailoverConfSVG } from '~media/navigation/failoverconf.svg'
import { ReactComponent as EventsSVG } from '~media/navigation/events.svg'
import { ReactComponent as CustomersSVG } from '~media/navigation/customers.svg'
import { ReactComponent as TeamSVG } from '~media/navigation/team.svg'
import { ReactComponent as PressSVG } from '~media/navigation/press.svg'
import { ReactComponent as LeadershipSVG } from '~media/navigation/leadership.svg'
import { ReactComponent as BlogSVG } from '~media/navigation/blog.svg'
import { ReactComponent as DocsSVG } from '~media/navigation/docs.svg'
import { ReactComponent as TutorialsSVG } from '~media/navigation/tutorials.svg'
import { ReactComponent as ResourcesSVG } from '~media/navigation/resources.svg'
import { ReactComponent as SecuritySVG } from '~media/navigation/security.svg'
import { ReactComponent as BootCampsSVG } from '~media/navigation/bootcamps.svg'
import { fadeIn } from '~styles/utility'

const DropdownContent = ({ size = 'base', ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        animationName: fadeIn(),
        animationDuration: theme.timing.long,
        animationTimingFunction: theme.transition.base,
        width: size === 'base' ? 480 : 680,
      }}
      {...rest}
    />
  )
}

DropdownContent.propTypes = {
  size: PropTypes.oneOf(['base', 'large']),
}

const DropdownGridWrapperWithAside = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: theme.space.base,
        alignSelf: 'start', // Helps with aside grid items
      }}
      {...props}
    />
  )
}

const DropdownGrid = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.space.base,
      }}
      {...props}
    />
  )
}

const DropdownGridItem = ({
  href,
  hrefSeparator = '?',
  hash,
  title,
  subText,
  badge,
  svg: SVGComponent,
}) => {
  const theme = useTheme()

  return (
    <div css={{ position: 'relative', alignSelf: 'start' }}>
      <TextLink
        href={`${href}${hrefSeparator}ref=nav${hash ? `#${hash}` : ''}`}
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,

          '&:hover, &:focus': {
            '+ .Global__Header__NavWrapper svg': {
              filter: 'grayscale(0)',
              opacity: 1,
            },
          },
        }}
      />
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          alignItems: 'start',
          gap: theme.space.small,
        }}
        className="Global__Header__NavWrapper"
      >
        <SVGComponent
          css={{
            width: theme.space.base,
            filter: 'grayscale(1)',
            opacity: theme.opacity.base,
            transition: `filter ${navTransition(
              theme
            )}, opacity ${navTransition(theme)}`,
          }}
        />
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: theme.space.xsmall,
          }}
        >
          <div
            css={{
              fontWeight: theme.fontWeight.bold,
              lineHeight: theme.lineHeight.small,
            }}
          >
            {title}
          </div>
          {subText && (
            <div
              css={{
                color: theme.fontColor.muted,
                ...theme.text.xsmall,
              }}
            >
              {subText}
            </div>
          )}
          {badge && (
            <Badge css={{ justifySelf: 'start' }} type="primary">
              {badge}
            </Badge>
          )}
        </div>
      </div>
    </div>
  )
}

DropdownGridItem.propTypes = {
  badge: PropTypes.string,
  href: PropTypes.string.isRequired,
  hrefSeparator: PropTypes.string,
  hash: PropTypes.string,
  svg: PropTypes.any.isRequired,
  subText: PropTypes.string,
  title: PropTypes.string.isRequired,
}

const DropdownGridAside = (props) => {
  const theme = useTheme()

  return (
    <aside
      css={{
        display: 'grid',
        margin: -theme.space.medium, // Offset of dropdown padding
        marginLeft: 0,
        overflow: 'hidden',
        borderBottomRightRadius: theme.borderRadius.large,
        borderTopRightRadius: theme.borderRadius.large,
      }}
      {...props}
    />
  )
}

const DropdownGridAsideBlockItem = ({ title, subText, href }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        background: generateGradient({
          start: theme.color.panel.dark,
          stop: theme.color.panel.base,
          position: 'to right',
        }),
        paddingTop: theme.space.base,
        paddingBottom: theme.space.base,
        paddingLeft: theme.space.medium,
        paddingRight: theme.space.medium,
        position: 'relative',
      }}
    >
      <TextLink
        href={`${href}?ref=nav`}
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      />
      <div
        css={{
          ...theme.text.small,
          fontWeight: theme.fontWeight.bold,
        }}
      >
        {title}
      </div>
      <div
        css={{
          color: theme.fontColor.muted,
          ...theme.text.xsmall,
          marginTop: theme.space.xsmall,
        }}
      >
        {subText}
      </div>
    </div>
  )
}

DropdownGridAsideBlockItem.propTypes = {
  href: PropTypes.string.isRequired,
  subText: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
}

const NavContent = ({ type }) => {
  switch (type) {
    case 'resources':
      return (
        <DropdownContent size="large">
          <DropdownGridWrapperWithAside>
            <DropdownGrid>
              <DropdownGridItem
                title="Blog"
                subText="The latest Chaos Engineering and product updates"
                href="/blog/"
                svg={BlogSVG}
              />
              <DropdownGridItem
                title="Docs"
                subText="Start Chaos Engineering with Gremlin"
                href="/docs/"
                svg={DocsSVG}
              />
              <DropdownGridItem
                title="Tutorials"
                subText="Step-by-step guides for Chaos Engineering"
                href="/community/tutorials/"
                svg={TutorialsSVG}
              />
              <DropdownGridItem
                title="Security"
                subText="See Gremlin’s security overview and practices"
                href="/security/"
                svg={SecuritySVG}
              />
              <DropdownGridItem
                title="Bootcamps"
                subText="Hands-on Chaos Engineering training "
                href="/bootcamps/"
                svg={BootCampsSVG}
              />
              <DropdownGridItem
                title="Search all"
                subText="Explore all guides, webinars, and more"
                href="/resources/"
                svg={ResourcesSVG}
              />
            </DropdownGrid>
            <DropdownGridAside>
              <div
                css={{
                  display: 'grid',
                  gap: 2,
                }}
              >
                <DropdownGridAsideBlockItem
                  title="What is Chaos Engineering?"
                  subText="Go beyond the buzz and learn the fundamentals"
                  href="/chaos-engineering/"
                />
                <DropdownGridAsideBlockItem
                  title="How to achieve reliability in distributed systems"
                  subText="Get a framework for addressing reliability in modern, distributed environments."
                  href="/reliability/"
                />
                <DropdownGridAsideBlockItem
                  title="The 2021 State of Chaos Engineering Report"
                  subText="Track the evolution of Chaos Engineering"
                  href="/state-of-chaos-engineering/2021/"
                />
              </div>
            </DropdownGridAside>
          </DropdownGridWrapperWithAside>
        </DropdownContent>
      )
    case 'community':
      return (
        <DropdownContent>
          <DropdownGrid>
            <DropdownGridItem
              title="Chaos Community"
              subText="Join our community of 5,000+ engineers on Slack"
              href="/community/"
              svg={SlackSVG}
            />
            <DropdownGridItem
              title="Chaos Conf"
              subText="The largest Chaos Engineering event in history"
              href="/chaos-conf/2020/"
              svg={ChaosConfSVG}
            />
            <DropdownGridItem
              title="Events"
              subText="Workshops, meetups, webinars and more"
              href="/resources/?refinementList%5BarticleLabel%5D%5B0%5D=Events&refinementList%5BarticleLabel%5D%5B1%5D=Webinars&page=1&configure%5BfacetingAfterDistinct%5D=true"
              hrefSeparator="&"
              svg={EventsSVG}
            />
            <DropdownGridItem
              title="Failover Conf"
              subText="Watch talks, panel discussions, and fireside chat from reliability experts."
              href="/failoverconf/2021/"
              svg={FailoverConfSVG}
            />
          </DropdownGrid>
        </DropdownContent>
      )
    case 'company':
      return (
        <DropdownContent>
          <DropdownGrid>
            <DropdownGridItem
              title="Customers"
              subText="Teams using Gremlin to improve reliability"
              href="/customers/"
              svg={CustomersSVG}
            />
            <DropdownGridItem
              title="Team &amp; Careers"
              badge="Join us"
              subText="Meet the team that makes Gremlin"
              href="/team/"
              svg={TeamSVG}
            />
            <DropdownGridItem
              title="Press"
              subText="News, press releases, &amp; media resources"
              href="/press/about/"
              svg={PressSVG}
            />
            <DropdownGridItem
              title="Leadership"
              subText="Our founders know what it's like to be paged at 2AM"
              href="/leadership/"
              svg={LeadershipSVG}
            />
          </DropdownGrid>
        </DropdownContent>
      )
    default:
      return null
  }
}

NavContent.propTypes = {
  type: PropTypes.string,
}

const NavParentItem = forwardRef(
  ({ as: Component = TextLink, contrast, isStuck, ...rest }, ref) => {
    const theme = useTheme()

    return (
      <div>
        {/* Parent item needed for Tippy.js context */}
        <Component
          ref={ref}
          css={[
            Component === 'div' && {
              cursor: 'default',
            },

            (contrast || isStuck) && {
              color: theme.contrast.base,
            },

            !contrast &&
              !isStuck && {
                color: theme.fontColor.base,
              },

            {
              whiteSpace: 'nowrap',
              paddingLeft: theme.space.small,
              paddingRight: theme.space.small,
              transition: `opacity ${navTransition(theme)}`,
              fontWeight: theme.fontWeight.bold,

              '&:hover, &:focus, &.is-active, &[aria-expanded="true"]': [
                {
                  opacity: theme.opacity.base,
                  color: theme.fontColor.base,
                },
                (contrast || isStuck) && {
                  color: theme.contrast.base,
                },
              ],

              [theme.mq.large]: {
                paddingLeft: theme.space.base,
                paddingRight: theme.space.base,
              },
            },
          ]}
          {...rest}
        />
      </div>
    )
  }
)

NavParentItem.propTypes = {
  as: PropTypes.any,
  contrast: PropTypes.bool,
  isStuck: PropTypes.bool,
}

const TippyItem = ({ content, ...rest }) => {
  const theme = useTheme()

  const tippyRef = useRef()

  const controls = useAnimation()

  const variants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
    },
  }

  const handleOnMount = () => {
    controls.start('visible')
  }

  const handleOnHide = async (instance) => {
    await controls.start('hidden')

    instance.unmount()
  }

  return (
    <Tippy
      interactive
      hideOnClick={false}
      offset={[0, theme.space.medium]}
      animation
      onMount={handleOnMount}
      onHide={handleOnHide}
      moveTransition={`transform ${navTransition(theme)}`}
      render={(attrs) => (
        <motion.div
          ref={tippyRef}
          css={{
            // @NOTE Most of this is copied from `<Dropdown />`, with some slight modifications to work with Tippy.js singletons
            background: generateGradient({
              start: theme.color.light.base,
              stop: theme.color.panel.light,
              position: 'to bottom',
            }),
            padding: theme.space.medium,
            color: theme.fontColor.base,
            borderRadius: theme.borderRadius.large,
            textAlign: 'left',
            zIndex: 10,
            filter: `drop-shadow(0 0 1px ${
              theme.color.border.base
            }) drop-shadow(2px 4px 6px ${rgba(theme.color.dark.base, 0.125)})`,
            transformOrigin:
              attrs['data-placement'] &&
              getTransformOrigin(attrs['data-placement']),
          }}
          tabIndex="-1"
          variants={variants}
          animate={controls}
          initial="hidden"
          transition={theme.motion.transition.base}
          {...attrs}
        >
          <div
            css={{
              // Dimensions taken from SVG directly
              background: `url(${blob}) no-repeat center center`,
              backgroundSize: 'contain',
              transition: `transform ${navTransition(theme)}`,
              width: 54,
              height: 28,
              top: -14, // To appear above nav
            }}
            data-popper-arrow
          />
          <NavContent type={content} />
        </motion.div>
      )}
      {...rest}
    />
  )
}

TippyItem.propTypes = {
  content: PropTypes.string.isRequired,
}

const DesktopNav = ({
  contrast,
  forceCTAContrast,
  isStuck,
  showCTA = true,
}) => {
  const theme = useTheme()

  return (
    <Navigation
      css={{
        gap: 0, // Easier transitions with hovers

        [theme.mq.small__max]: { display: 'none' },
      }}
    >
      <NavParentItem
        href="/product/?ref=nav"
        contrast={contrast}
        isStuck={isStuck}
      >
        Product
      </NavParentItem>
      <NavParentItem
        href="/pricing/?ref=nav"
        contrast={contrast}
        isStuck={isStuck}
      >
        Pricing
      </NavParentItem>
      <TippyItem placement="bottom" content="resources">
        <NavParentItem as="div" contrast={contrast} isStuck={isStuck}>
          Resources
        </NavParentItem>
      </TippyItem>
      <TippyItem placement="bottom" content="community">
        <NavParentItem as="div" contrast={contrast} isStuck={isStuck}>
          Community
        </NavParentItem>
      </TippyItem>
      <TippyItem placement="bottom-end" content="company">
        <NavParentItem as="div" contrast={contrast} isStuck={isStuck}>
          Company
        </NavParentItem>
      </TippyItem>
      <NavParentItem
        contrast={contrast}
        isStuck={isStuck}
        href="https://app.gremlin.com?ref=nav"
        css={{
          display: 'block',

          [theme.mq.large]: {
            marginRight: theme.space.small + theme.space.xsmall,
          },
        }}
      >
        Login
        <Icon
          size="small"
          css={{
            marginLeft: theme.space.small,

            [theme.mq.medium__max]: { display: 'none' },
          }}
          icon="arrow-right"
        />
      </NavParentItem>
      {showCTA && (
        <ButtonLink
          noContrast={!forceCTAContrast}
          type="primary"
          url="/get-started/?ref=nav"
          size="xsmall"
        >
          Get started
        </ButtonLink>
      )}
    </Navigation>
  )
}

DesktopNav.propTypes = {
  contrast: PropTypes.bool,
  forceCTAContrast: PropTypes.bool,
  isStuck: PropTypes.bool,
  showCTA: PropTypes.bool,
}

export default DesktopNav
